import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  HelpCircle, 
  ChevronRight, 
  ChevronLeft, 
  X,
  Settings,
  Users,
  Gift,
  Play,
  Trophy,
  Share2,
  CheckCircle2,
  Sparkles,
  Target,
  Clock,
  Image,
  Bell
} from "lucide-react";

const GUIDE_STEPS = [
  {
    id: "intro",
    phase: "intro",
    title: "Bienvenue dans l'assistant de tirage",
    icon: Sparkles,
    content: `Ce guide vous accompagne pas à pas dans la création et la gestion de votre tirage au sort.
    
Un tirage se déroule en **3 phases** :
1. **Configuration** : Paramétrez votre tirage
2. **Collecte** : Récoltez les participants
3. **Tirage & Résultats** : Lancez le tirage et diffusez les gagnants`,
    tips: [
      "Vous pouvez revenir sur ce guide à tout moment",
      "Chaque étape est sauvegardée automatiquement"
    ]
  },
  {
    id: "settings",
    phase: "before",
    title: "1. Configuration générale",
    icon: Settings,
    content: `Commencez par définir les informations de base de votre tirage :

• **Titre** : Le nom de votre opération (visible par les participants)
• **Description** : Expliquez le contexte et les règles
• **Logo** : Votre logo ou celui de l'événement
• **Dates** : Définissez les dates de participation et de tirage`,
    tips: [
      "Un titre accrocheur augmente la participation",
      "Soyez clair sur les conditions de participation"
    ]
  },
  {
    id: "appearance",
    phase: "before",
    title: "2. Personnalisation visuelle",
    icon: Image,
    content: `Rendez votre tirage unique avec une personnalisation complète :

• **Couleurs** : Choisissez vos couleurs de marque
• **Arrière-plan** : Image, couleur unie ou vidéo YouTube
• **Partenaires** : Ajoutez les logos de vos partenaires

L'aperçu en temps réel vous montre le rendu final.`,
    tips: [
      "Utilisez une vidéo YouTube pour un effet impressionnant",
      "Les logos partenaires peuvent venir de votre galerie média"
    ]
  },
  {
    id: "prizes",
    phase: "before",
    title: "3. Définition des lots",
    icon: Gift,
    content: `Configurez les lots à gagner :

• **Nom et description** : Décrivez chaque lot
• **Quantité** : Combien de ce lot à distribuer
• **Image/Vidéo** : Illustrez vos lots
• **Partenaire** : Indiquez le sponsor du lot
• **Instructions de retrait** : Comment récupérer le lot`,
    tips: [
      "Des photos de qualité motivent les participants",
      "Précisez bien les modalités de retrait"
    ]
  },
  {
    id: "participants",
    phase: "during",
    title: "4. Collecte des participants",
    icon: Users,
    content: `Plusieurs modes de collecte disponibles :

• **Lien public** : Partagez un lien d'inscription
• **QR Code** : Idéal pour les événements physiques
• **Mode boutique** : Inscription rapide en caisse
• **Import fichier** : CSV/Excel pour des listes existantes
• **Ajout manuel** : Saisissez les participants un par un`,
    tips: [
      "Activez le QR code pour les événements en présentiel",
      "L'import fichier nécessite une attestation RGPD"
    ]
  },
  {
    id: "participation",
    phase: "during",
    title: "5. Suivi en temps réel",
    icon: Clock,
    content: `Pendant la phase de collecte :

• **Compteur** : Suivez le nombre d'inscrits en direct
• **Statistiques** : Taux de qualification (emails, téléphones)
• **Page publique** : Les participants voient un message d'attente
• **Clôture** : La deadline ferme automatiquement les inscriptions`,
    tips: [
      "Relancez sur les réseaux sociaux si les inscriptions stagnent",
      "Le mur photos peut créer de l'engagement"
    ]
  },
  {
    id: "draw",
    phase: "after",
    title: "6. Lancement du tirage",
    icon: Play,
    content: `Le moment crucial ! Deux modes disponibles :

• **Aléatoire** : Chaque lot est attribué à un gagnant tiré au hasard
• **Par ordre d'inscription** : Le 1er inscrit a le 1er lot, etc.

Le tirage est **définitif** et **horodaté** pour garantir la transparence.`,
    tips: [
      "Vérifiez vos lots et participants avant de lancer",
      "Le tirage ne peut pas être annulé"
    ]
  },
  {
    id: "results",
    phase: "after",
    title: "7. Gestion des résultats",
    icon: Trophy,
    content: `Après le tirage :

• **Liste des gagnants** : Tous les résultats avec références
• **Statut de retrait** : Marquez les lots récupérés
• **Relances** : Envoyez des rappels aux retardataires
• **Export** : Téléchargez la liste en CSV`,
    tips: [
      "Relancez les gagnants qui n'ont pas retiré leur lot",
      "Vous pouvez annuler un lot non réclamé"
    ]
  },
  {
    id: "broadcast",
    phase: "after",
    title: "8. Diffusion des résultats",
    icon: Share2,
    content: `Partagez les résultats :

• **Page SHOW** : Affichage grand écran pour événements
• **Diffusion** : Activez/désactivez l'affichage public
• **Notifications** : Informez automatiquement les gagnants par email
• **Réseaux sociaux** : Partagez le lien des résultats`,
    tips: [
      "La page SHOW est parfaite pour les écrans en magasin",
      "Configurez vos emails avant la diffusion"
    ]
  },
  {
    id: "complete",
    phase: "complete",
    title: "Vous êtes prêt !",
    icon: CheckCircle2,
    content: `Félicitations ! Vous connaissez maintenant toutes les étapes d'un tirage au sort réussi.

**Récapitulatif** :
1. Configurez votre tirage (titre, dates, apparence)
2. Ajoutez vos lots à gagner
3. Collectez les participants
4. Lancez le tirage
5. Gérez et diffusez les résultats

Bonne chance pour votre tirage !`,
    tips: [
      "Ce guide reste accessible via le bouton d'aide",
      "Contactez-nous si vous avez des questions"
    ]
  }
];

const PHASES = [
  { id: "intro", label: "Introduction", color: "bg-blue-500" },
  { id: "before", label: "Avant le tirage", color: "bg-amber-500" },
  { id: "during", label: "Pendant", color: "bg-emerald-500" },
  { id: "after", label: "Après le tirage", color: "bg-purple-500" },
  { id: "complete", label: "Terminé", color: "bg-green-500" }
];

export default function DrawGuide({ trigger, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenGuide, setHasSeenGuide] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem('tireo_draw_guide_seen');
    setHasSeenGuide(!!seen);
  }, []);

  const step = GUIDE_STEPS[currentStep];
  const StepIcon = step.icon;
  const currentPhase = PHASES.find(p => p.id === step.phase);

  const nextStep = () => {
    if (currentStep < GUIDE_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const closeGuide = () => {
    setOpen(false);
    localStorage.setItem('tireo_draw_guide_seen', 'true');
    setHasSeenGuide(true);
  };

  const resetAndOpen = () => {
    setCurrentStep(0);
    setOpen(true);
  };

  return (
    <>
      {/* Trigger button */}
      {trigger ? (
        <div onClick={resetAndOpen}>{trigger}</div>
      ) : (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={resetAndOpen}
          className="gap-2"
          data-testid="draw-guide-trigger"
        >
          <HelpCircle className="w-4 h-4" />
          Guide
          {!hasSeenGuide && (
            <Badge className="bg-primary text-primary-foreground text-xs px-1.5">Nouveau</Badge>
          )}
        </Button>
      )}

      {/* Guide Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            {/* Phase indicator */}
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`${currentPhase.color} text-white`}>
                {currentPhase.label}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Étape {currentStep + 1} / {GUIDE_STEPS.length}
              </span>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-muted rounded-full h-1.5 mb-4">
              <div 
                className="bg-primary h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / GUIDE_STEPS.length) * 100}%` }}
              />
            </div>

            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className={`w-10 h-10 rounded-xl ${currentPhase.color} flex items-center justify-center`}>
                <StepIcon className="w-5 h-5 text-white" />
              </div>
              {step.title}
            </DialogTitle>
          </DialogHeader>

          {/* Content */}
          <div className="flex-1 overflow-y-auto py-4">
            <div className="prose prose-sm dark:prose-invert max-w-none">
              {step.content.split('\n').map((paragraph, idx) => (
                <p key={idx} className="whitespace-pre-line text-foreground/90">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Tips */}
            {step.tips && step.tips.length > 0 && (
              <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
                <h4 className="font-semibold text-sm flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-primary" />
                  Conseils
                </h4>
                <ul className="space-y-1">
                  {step.tips.map((tip, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <CheckCircle2 className="w-3 h-3 text-primary mt-1 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Navigation */}
          <DialogFooter className="flex-shrink-0 flex items-center justify-between border-t pt-4 mt-4">
            <Button
              variant="ghost"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Précédent
            </Button>

            <div className="flex gap-1">
              {GUIDE_STEPS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'bg-primary w-4' 
                      : 'bg-muted hover:bg-muted-foreground/50'
                  }`}
                />
              ))}
            </div>

            {currentStep === GUIDE_STEPS.length - 1 ? (
              <Button onClick={closeGuide} className="gap-2">
                Terminer
                <CheckCircle2 className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={nextStep} className="gap-2">
                Suivant
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Export a floating help button that can be placed anywhere
export function DrawGuideFloatingButton() {
  const [hasSeenGuide, setHasSeenGuide] = useState(true);

  useEffect(() => {
    const seen = localStorage.getItem('tireo_draw_guide_seen');
    setHasSeenGuide(!!seen);
  }, []);

  return (
    <DrawGuide 
      trigger={
        <Button
          variant="outline"
          size="icon"
          className="fixed bottom-20 right-4 z-40 rounded-full w-12 h-12 shadow-lg bg-card hover:bg-accent"
          data-testid="draw-guide-floating"
        >
          <HelpCircle className="w-5 h-5" />
          {!hasSeenGuide && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-pulse" />
          )}
        </Button>
      }
    />
  );
}
